import { useCallback, useMemo, useState } from 'react'
import Select from 'react-select'
import type { Entry, EntryCollection } from 'contentful'
import { cubicBezier, motion } from 'framer-motion'

import Container from '@/components/container'
import PageWrapper from '@/components/page-wrapper'
import ArticleListCard from '@/components/article-list-card'

import type { TypeEventsSkeleton } from '@/types/ctf'
import { cx, slugify } from '@/utils/strings'
import useGetArticles from '@/utils/useGetArticles'
import type { TagObject } from '@/utils/tags'
import { usePage } from '@inertiajs/react'
import CtaLink, { buttonStyles } from '@/components/ui/cta-link'
import colors from '@/constants/colors'
import { useTranslations } from '@/utils/locale'
import ArticleListHeader from '@/components/article-list-header'

type EventListProps = {
  posts: EntryCollection<TypeEventsSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
  tags: TagObject[]
  seo: any
  headerNav: any
  footerNav: any
  headerBanners: any
  newsletterForm: any
}

type TypeEvent = Entry<TypeEventsSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>

type AddedEventNode = Omit<TypeEvent['fields'], 'eventDate'> & {
  heading: string
  label: string
  eventDate?: Date
}

enum EventState {
  IN_PROGRESS = 'in-progress',
  UPCOMING = 'upcoming',
  PAST = 'past',
  FEATURED = 'featured',
}

export default function EventList({
  seo = { fields: {} },
  posts,
  tags,
  headerNav,
  footerNav,
  newsletterForm,
  headerBanners,
}: EventListProps) {
  const { t } = useTranslations()
  const EVENT_STATE = {
    [EventState.IN_PROGRESS]: {
      type: EventState.IN_PROGRESS,
      heading: t('In Progress Events'),
      label: t('In Progress'),
    },
    [EventState.UPCOMING]: {
      type: EventState.UPCOMING,
      heading: t('Upcoming Events'),
      label: t('Live'),
    },
    [EventState.PAST]: {
      type: EventState.PAST,
      heading: t('Past Events'),
      label: t('Replay'),
    },
    [EventState.FEATURED]: {
      type: EventState.FEATURED,
      heading: t('Featured Events'),
      label: t('Featured'),
    },
  }

  const eventNodes = posts.items

  const page = usePage()
  const { numVisible, setNumVisible, numPerPage, selectedTag, setSelectedTag, didMountState } = useGetArticles(page.url)
  const [reactSelectedTag, setReactSelectedTag] = useState<{
    value: string
    label: string
    count: number
  } | null>(null)

  const getEventState = useCallback((event: TypeEvent) => {
    const endDate = Number(new Date(event.fields.eventEndDate)) || null
    const twoHourInMilliseconds = 1000 * 60 * 60 * 2
    const eventTimestamp = Number(new Date(event.fields.eventDate || 0))
    const eventEndTimestamp = endDate ?? eventTimestamp + twoHourInMilliseconds
    const nowTimestamp = Number(new Date())

    if (eventTimestamp < nowTimestamp && eventEndTimestamp > nowTimestamp) {
      return EVENT_STATE[EventState.IN_PROGRESS]
    } else if (eventEndTimestamp > nowTimestamp) {
      return EVENT_STATE[EventState.UPCOMING]
    } else {
      return EVENT_STATE[EventState.PAST]
    }
  }, [])

  const demos = useMemo(
    () =>
      eventNodes
        .filter((event) => event.fields.tags?.includes('Demo'))
        .sort(
          (a, b) =>
            Number(new Date(b.fields.eventDate as unknown as string)) -
            Number(new Date(a.fields.eventDate as unknown as string))
        )
        .reduce(
          (acc, next) => {
            const tags = next.fields.tags || []
            const isFeatured = next.fields.featuredEvent
            if (isFeatured) {
              if (!acc.featuredProductDemo && tags.includes('Product')) {
                const state = getEventState(next)
                acc.featuredProductDemo = {
                  state,
                  event: next,
                }
              }
              if (!acc.featuredMspDemo && tags.includes('MSP')) {
                const state = getEventState(next)
                acc.featuredMspDemo = {
                  state,
                  event: next,
                }
              }
              if (!acc.featuredEnterpriseDemo && tags.includes('Enterprise')) {
                const state = getEventState(next)
                acc.featuredEnterpriseDemo = {
                  state,
                  event: next,
                }
              }
            } else {
              if (!acc.productDemo && tags.includes('Product')) {
                const state = getEventState(next)
                acc.productDemo = {
                  state,
                  event: next,
                }
              }
              if (!acc.mspDemo && tags.includes('MSP')) {
                const state = getEventState(next)
                acc.mspDemo = {
                  state,
                  event: next,
                }
              }
              if (!acc.enterpriseDemo && tags.includes('Enterprise')) {
                const state = getEventState(next)
                acc.enterpriseDemo = {
                  state,
                  event: next,
                }
              }
            }
            return acc
          },
          {
            productDemo: null,
            featuredProductDemo: null,
            mspDemo: null,
            featuredMspDemo: null,
            enterpriseDemo: null,
            featuredEnterpriseDemo: null,
          } as Record<
            string,
            {
              state: ReturnType<typeof getEventState>
              event: TypeEvent
            } | null
          >
        ),
    [eventNodes]
  )
  const productDemo = demos.featuredProductDemo || demos.productDemo
  const mspDemo = demos.featuredMspDemo || demos.mspDemo
  const enterpriseDemo = demos.featuredEnterpriseDemo || demos.enterpriseDemo

  let featuredEvents = useMemo(
    () =>
      eventNodes
        .filter((event) => event.fields.featuredEvent)
        .sort(
          (a, b) =>
            Number(new Date(a.fields.eventDate as unknown as string)) -
            Number(new Date(b.fields.eventDate as unknown as string))
        ),
    [eventNodes]
  )

  // Filter out the product demo events from the featured events
  // This is to avoid showing the product demo events twice
  const productDemoIds = [productDemo?.event.sys.id, mspDemo?.event.sys.id, enterpriseDemo?.event.sys.id]
  featuredEvents = featuredEvents.filter((event) => {
    if (productDemoIds.includes(event.sys.id)) {
      return false
    }
    return true
  })

  const eventNodesOrdered = useMemo(() => {
    const futureEventNodes: AddedEventNode[] = []
    const inProgressEventNodes: AddedEventNode[] = []
    const pastEventNodes: AddedEventNode[] = []
    const createEventNode = ({
      heading,
      label,
      eventNode,
    }: {
      heading: string
      label: string
      eventNode: TypeEvent
    }): AddedEventNode => ({
      ...eventNode.fields,
      heading,
      label,
      eventDate: new Date(eventNode.fields.eventDate?.toString()),
    })

    eventNodes.forEach((eventNode) => {
      const state = getEventState(eventNode)

      if (state.type === EventState.IN_PROGRESS) {
        inProgressEventNodes.push(
          createEventNode({
            ...state,
            eventNode,
          })
        )
      } else if (state.type === EventState.UPCOMING) {
        futureEventNodes.push(
          createEventNode({
            ...state,
            eventNode,
          })
        )
      } else {
        pastEventNodes.push(
          createEventNode({
            ...state,
            eventNode,
          })
        )
      }
    })

    futureEventNodes.sort((a, b) => {
      if ((a.eventDate || 0) < (b.eventDate || 0)) return -1
      return 1
    })

    const featuredNodes = !selectedTag
      ? featuredEvents.map((eventNode) => {
          return createEventNode({
            ...EVENT_STATE[EventState.FEATURED],
            eventNode,
          })
        })
      : []

    return [...inProgressEventNodes, ...futureEventNodes, ...pastEventNodes]
  }, [eventNodes, featuredEvents, selectedTag])

  const events = useMemo(
    () =>
      eventNodesOrdered
        .filter(
          (event) =>
            !selectedTag ||
            selectedTag === 'all' ||
            event.tags?.map((tag) => tag.toLowerCase())?.includes(selectedTag.replaceAll('-', ' '))
        )
        .map((event) => ({
          ...event,
          eventDate: event?.eventDate ? new Date(event?.eventDate.toString()) : null,
        })),
    [eventNodesOrdered, selectedTag]
  )

  const selectTags =
    Object.values(tags || {})
      .map((tag) => ({ value: slugify(tag.name), label: `${tag.name} (${tag.count})`, count: tag.count }))
      .sort((a, b) => a.label.localeCompare(b.label)) || []
  selectTags.unshift({ label: 'All', value: '', count: eventNodes.length })

  return (
    <PageWrapper
      headerNav={headerNav}
      footerNav={footerNav}
      headerBanners={headerBanners}
      newsletterForm={newsletterForm}
      seo={seo}
      bodyTw="bg-secondary-50 dark:bg-secondary-900"
    >
      <ArticleListHeader heading="Bitwarden Events">
        <p className="mb-10">Learn how to get the most out of Bitwarden through live events and on-demand webcasts.</p>

        {!selectedTag && (productDemo || mspDemo || enterpriseDemo) && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-6 place-items-stretch [&_article]:h-full">
            {productDemo && (
              <ArticleListCard
                tags={['Demo']}
                image={productDemo.event.fields.seo?.fields?.ogImage || null}
                title={t('Bitwarden Product Deep Dive')}
                url={`/events/${productDemo.event.fields.slug}`}
                cta={
                  productDemo.state.type === EventState.PAST ? t('Watch the latest live demo') : t('Join the live demo')
                }
              />
            )}
            {mspDemo && (
              <ArticleListCard
                tags={['Demo']}
                image={mspDemo.event.fields.seo?.fields?.ogImage || null}
                title={mspDemo.event.fields.seo?.fields.title || ''}
                url={`/events/${mspDemo.event.fields.slug}`}
                cta={
                  mspDemo.state.type === EventState.PAST ? t('Watch the latest MSP demo') : t('Join the next MSP demo')
                }
              />
            )}
            {enterpriseDemo && (
              <ArticleListCard
                tags={['Demo']}
                image={enterpriseDemo.event.fields.seo?.fields?.ogImage || null}
                title={enterpriseDemo.event.fields.seo?.fields.title || ''}
                url={`/events/${enterpriseDemo.event.fields.slug}`}
                cta={
                  enterpriseDemo.state.type === EventState.PAST
                    ? t('Watch the latest Enterprise demo')
                    : t('Join the next Enterprise demo')
                }
              />
            )}
          </div>
        )}
      </ArticleListHeader>

      <Container className="lg:px-10">
        <div className="grid sm:justify-items-end py-4 lg:py-5 lg:mb-8">
          <div className="sm:flex items-center gap-2">
            <div>Filter events by tag:</div>
            <Select
              name="tags"
              value={reactSelectedTag}
              options={selectTags}
              formatOptionLabel={({ value, label, count }) => (
                <span>
                  {value === '' ? label : label.split(' (')[0]}
                  <span className="text-muted text-xs"> ({count})</span>
                </span>
              )}
              onChange={(selected) => {
                setNumVisible(numPerPage)
                if (selected) {
                  setSelectedTag(selected?.value)
                  setReactSelectedTag(selected)
                }
              }}
              isClearable={false}
              classNamePrefix="react-select"
              className="w-80 "
            />
          </div>
        </div>
        <section>
          {
            //render the next header if the selected tag is 'all'
            !selectedTag && (
              <>
                <h2 className="mb-6 lg:mb-10 h2">Featured events</h2>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {featuredEvents.map((event) => {
                    return (
                      <ArticleListCard
                        url={`/events/${event.fields.slug}/`}
                        image={event?.fields?.seo?.fields?.ogImage || null}
                        title={event?.fields?.seo?.fields?.title ?? ''}
                        description={event.fields.seo?.fields?.description?.toString()}
                        cta="Learn More"
                        eventDate={event.fields.eventDate}
                        tags={event.fields.tags}
                      />
                    )
                  })}
                </div>
              </>
            )
          }

          <div className={cx('flex flex-col gap-6 pb-10', !didMountState && 'invisible')}>
            {events.slice(0, numVisible).map((event, index, elements) => {
              const prevEvent = elements[index - 1]
              return (
                <div key={`${event.heading}-${event.internalName}`}>
                  {prevEvent?.heading !== event.heading && <h2 className="my-6 md:my-10 h2">{event.heading}</h2>}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{
                      duration: 0.15,
                      ease: cubicBezier(0, 0, 0.2, 1),
                    }}
                  >
                    <ArticleListCard
                      url={`/events/${event.slug}/`}
                      key={`${event.slug}${selectedTag}`}
                      image={event?.seo?.fields?.ogImage || null}
                      title={event?.seo?.fields?.title ?? ''}
                      description={event.seo?.fields?.description?.toString()}
                      cta="Learn More"
                      eventDate={event.eventDate}
                      tags={event.tags}
                    />
                  </motion.div>
                </div>
              )
            })}
            <div>
              {numVisible < events.length && (
                <div className="text-center mt-2">
                  <CtaLink
                    onClick={() => setNumVisible(numVisible + numPerPage)}
                    asButton
                    style={buttonStyles.btnSolid}
                  >
                    Load More
                  </CtaLink>
                </div>
              )}
            </div>
          </div>
        </section>
      </Container>
    </PageWrapper>
  )
}
